/**
 * bodyMotionNav — the navmesh -> kimodo bridge.
 *
 * This is the "bridge owns navmesh projection" seam (J2 / ENG-705): kimodo has
 * no navmesh concept, so path following happens entirely here —
 *
 *   1. find a path across the environment navmesh (NavMesh.findPath) -> waypoints
 *   2. send those waypoints to kimodo as a `root2d` constraint (the input)
 *   3. read the realized path back from the response `root` track (the output)
 *   4. snap that path onto the navmesh for the returned `waypoints` (soft
 *      constraint drifts a few cm — projection is ours, kimodo stays
 *      navmesh-agnostic). Playback uses the raw root: snapping quantizes to
 *      grid node centers, which pops the pelvis between cells.
 *
 * Exposes on window (dev + E2E hooks):
 *   __GEN_BODY_MOTION__(prompt, opts)                    — plain prompt -> play
 *   __GEN_BODY_MOTION_ON_NAVMESH__({prompt, goal, …})    — navmesh start->goal
 *   __GEN_BODY_MOTION_ON_WAYPOINTS__({prompt, waypoints}) — multi-waypoint route
 *
 * Requests go to `${KIMODO_BASE}/generate`. KIMODO_BASE is the relative
 * `/kimodo` prefix, resolved against the SPA's own origin in every mode: the
 * vite dev proxy forwards it in dev, and deployed it's nginx's `/kimodo/`
 * location (`nginx.conf.template`) → `${CONVORCHER_URL}/kimodo/`, which fronts
 * the kimodo service. Both hops must exist — a deploy missing the nginx block
 * answers the POST with the SPA fallback's 405. An E2E harness can point a
 * deployed app at a specific origin at runtime via `window.__KIMODO_BASE__`.
 */
import * as THREE from 'three'
import { buildBodyMotionClip, buildRootDrive, computeRootDelta, computeYawDelta, playGeneratedClip } from './bodyMotionClip.js'
import { resolveGeneratedRootMode } from './bodyMotionRootMode.js'
import { resolveAvatarTransformTarget } from './avatarTransformTarget.js'
import { ensureLiveBodyRig } from './liveBodyRig.js'
import {
  DEFAULT_CONSTRAINT_STRIDE, DEFAULT_CONSTRAINT_WEIGHT, DEFAULT_SMOOTH,
  buildRouteConstraint, clampConstraintWeight, clampRouteDuration, distXZ,
  planWaypointRoute, rotXZ,
} from './bodyMotionPath.js'

const KIMODO_BASE = '/kimodo'
const FPS = 30 // kimodo SOMA models are fixed 30 fps (service/SPEC.md)
const CLIP_NAME = 'kimodo_generated' // single reusable slot in __AVATAR_ACTIONS__

/** Build the generated tracks into a mixer clip and play it via __DIRECT_PLAY__.
 *  Locomotion clips commit their world root travel to the avatar group on
 *  finish (so the avatar STAYS at the destination instead of snapping back to
 *  the pre-clip position when the base pose resumes). Override via playOpts.
 *
 *  `playOpts.keepRootMotion === false` previews the clip the way SAVING it in place
 *  would store it: no pelvis position track, and nothing driving the avatar group
 *  either. Defaults to keeping the motion, so walk-here and the __GEN_BODY_MOTION_ON_*
 *  seams — which never set it — are unchanged. The mode itself is resolved by
 *  bodyMotionRootMode, shared with bodyMotionExport, because these two deciding it
 *  separately is exactly how the preview came to ignore the author's flag. */
function playTracks(tracks, playOpts) {
  const { keepRootMotion = true, ...clipOpts } = playOpts ?? {}
  // Root motion EXTRACTED: the clip holds a pose, the group holds where the
  // character is and which way it faces, updated every frame. Nothing is
  // committed at the end because nothing was ever deferred — which is also what
  // lets the clip blend out (see blendOutMs in playGeneratedClip).
  const rootDrive = buildRootDrive(tracks)
  const rootMode = resolveGeneratedRootMode({ keepRootMotion, live: true, hasRootDrive: !!rootDrive })
  if (rootMode === 'extract') {
    return playGeneratedClip(
      buildBodyMotionClip(tracks, { name: CLIP_NAME, rootMode }),
      // rootYawPath: extract stripped the turn out of the pose, so the group carries it.
      { rootDrive, ...rootTravel(tracks), rootYawPath: rootDrive.dyaw, ...clipOpts },
    )
  }
  // In place: the pelvis track is never built, AND the group is left alone. Driving the
  // group here would walk the character across the floor while the clip this preview
  // stands for holds still — the same lie one layer down.
  if (rootMode === 'lock') {
    return playGeneratedClip(buildBodyMotionClip(tracks, { name: CLIP_NAME, rootMode }), { ...clipOpts })
  }
  // No root track to extract (an in-place gesture): the old path, unchanged.
  return playGeneratedClip(
    buildBodyMotionClip(tracks, { name: CLIP_NAME, rootMode }),
    { ...rootTravel(tracks), ...clipOpts },
  )
}

/** The same travel, for the rig whose render loop drives the group itself.
 *  A part rig never sees rootDrive — MultipartOGSModel owns it, not AvatarModel —
 *  so it walks rootPath, kimodo's own route, rather than a line to the endpoint. */
function rootTravel(tracks) {
  return {
    commitRootDelta: computeRootDelta(tracks),
    commitYawDelta: computeYawDelta(tracks),
    rootPath: tracks?.root ?? null,
    rootFrames: tracks?.frames ?? 0,
  }
}

/** Stop the generated preview clip if it currently holds the __DIRECT_PLAY__
 *  slot, returning the avatar to its base pose immediately instead of letting
 *  the clip run out its self-clearing envelope. Fires any pending root-motion
 *  commit first (a locomotion preview still lands at its travelled spot). No-op
 *  when nothing generated is playing, so it's safe to call unconditionally. */
function stopPreview() {
  if (typeof window === 'undefined') return
  const dp = window.__DIRECT_PLAY__
  if (!dp || dp.clipName !== CLIP_NAME) return
  window.__DIRECT_PLAY__ = null
  if (typeof dp.onExpire === 'function') {
    try { dp.onExpire() } catch (err) { console.warn('[bodyMotion] stopPreview onExpire threw:', err) }
  }
  const action = (window.__AVATAR_ACTIONS__ || {})[CLIP_NAME]
  if (action) { try { action.stop() } catch { /* ignore */ } }
}

function kimodoBase() {
  // Runtime override wins (lets an E2E harness point the deployed app at a
  // specific convorcher without a rebuild); else the relative `/kimodo` base.
  return (typeof window !== 'undefined' && window.__KIMODO_BASE__) || KIMODO_BASE
}

/**
 * POST a request body to kimodo /generate and return the tracks payload.
 *
 * Auth mirrors apiClient.js / agentApi.js (ENG-1046): the avataros_session
 * cookie rides along (`credentials: 'include'`), and a 401 gets ONE
 * /auth/refresh + retry before failing. This call used a bare fetch() and had
 * neither, which made it the only authenticated path in the SPA that could not
 * survive an expired session — convorcher's resolveCaller is cookie-FIRST and
 * deliberately does NOT fall through to the nginx-injected X-API-Key when a
 * cookie is present but invalid (app/controllers/admin_api.go), so a stale
 * cookie is a hard 401 here even though every /api call kept working.
 *
 * The refresh path is relative, same as agentApi.js: nginx's `location /auth/`
 * owns that hop deployed. There is no /auth dev-proxy entry, so in `npm run
 * dev` the refresh 404s and the retry is skipped — harmless, because the dev
 * proxy injects VITE_CONVORCHER_API_KEY and never relies on a cookie.
 */
async function postGenerate(body) {
  const url = `${kimodoBase().replace(/\/$/, '')}/generate`
  const t0 = performance.now()
  const init = {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }
  let res = await fetch(url, init)
  if (res.status === 401) {
    const refresh = await fetch('/auth/refresh', { method: 'POST', credentials: 'include' })
    if (!refresh.ok) {
      console.error(`[bodyMotion] kimodo /generate 401 and /auth/refresh ${refresh.status}`)
      throw new Error('Session expired — sign in again, then re-run Generate.')
    }
    res = await fetch(url, init)
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    console.error(`[bodyMotion] kimodo /generate ${res.status}: ${text}`)
    throw new Error(`kimodo /generate failed: ${res.status}`)
  }
  const tracks = await res.json()
  console.log(
    `[bodyMotion] ${tracks.frames}f / ${Object.keys(tracks.bones || {}).length} bones in ` +
    `${((performance.now() - t0) / 1000).toFixed(1)}s`,
  )
  return tracks
}

/**
 * Plain prompt -> motion, no path constraint. Kept as the simplest dev hook.
 *   await __GEN_BODY_MOTION__('wave hello', { duration_sec: 3 })
 */
async function genBodyMotion(prompt, opts = {}) {
  if (!prompt) { console.warn('[bodyMotion] __GEN_BODY_MOTION__: prompt required'); return null }
  const body = {
    prompt,
    duration_sec: opts.duration_sec ?? 4,
    diffusion_steps: opts.diffusion_steps ?? 100,
    ...(opts.seed != null ? { seed: opts.seed } : {}),
    ...(opts.footlock != null ? { footlock: opts.footlock } : {}),
  }
  // The clip is BAKED against the rig it plays on, so on a splat character the part
  // rig has to be mounted before playTracks can build anything — asked for here, in
  // parallel with the generation, so the download costs no extra wall clock. A no-op
  // on a GLB avatar and on a rig that is already up.
  const rigReady = ensureLiveBodyRig()
  const tracks = await postGenerate(body)
  await rigReady
  tracks.clipName = playTracks(tracks, opts.play)
  return tracks
}

// ── navmesh path helpers ─────────────────────────────────────────────────────

function getNavmesh() {
  const nm = typeof window !== 'undefined' ? window.__NAVMESH_INSTANCE__ : null
  if (!nm) throw new Error('navmesh not loaded (window.__NAVMESH_INSTANCE__ is null)')
  return nm
}

/** Current avatar world position [x,y,z] from the pelvis bone. Throws if the
 *  avatar isn't mounted yet — a silent [0,0,0] fallback would plan the path
 *  from the world origin instead of where the avatar stands (CLAUDE.md: no
 *  silent fallbacks). Pass o.from explicitly to bypass. */
function currentAvatarXZ() {
  const scene = typeof window !== 'undefined' ? window.__AVATAR_SCENE__ : null
  const pelvis = scene && scene.getObjectByName('pelvis')
  if (!pelvis) {
    throw new Error('avatar not ready (no pelvis) — pass an explicit `from` [x,y,z]')
  }
  pelvis.updateWorldMatrix(true, false)
  const e = pelvis.matrixWorld.elements // column-major; translation is [12,13,14]
  return [e[12], e[13], e[14]]
}

/**
 * The 23 MetaHuman bones kimodo's rotation constraints accept — the retarget
 * bone_map values (`soma_mh_retarget/mapping.py`, SOMA_MH_PAIRS). A name outside
 * this set is a 422 listing the offenders, so the list is transcribed rather
 * than guessed, and a bone the rig happens not to have is simply omitted.
 */
export const POSE_LOCK_BONES = [
  'pelvis', 'spine_01', 'spine_03', 'spine_05', 'neck_01', 'neck_02', 'head',
  'clavicle_l', 'upperarm_l', 'lowerarm_l', 'hand_l',
  'clavicle_r', 'upperarm_r', 'lowerarm_r', 'hand_r',
  'thigh_l', 'calf_l', 'foot_l', 'ball_l',
  'thigh_r', 'calf_r', 'foot_r', 'ball_r',
]

/** Opening frames held at the character's current pose. */
export const DEFAULT_POSE_LOCK_FRAMES = 3

/**
 * A kimodo `fullbody` constraint pinning the opening frames to the character's
 * CURRENT pose — so a generated clip starts from what the avatar is already
 * doing instead of from the model's own choice of opening frame.
 *
 * `fullbody` is the constraint family directly (`service/SPEC.md`), NOT the
 * `seed_pose` convenience wrapper. Same machinery — the wrapper is expanded into
 * exactly this server-side — but built here the FRAMES are ours to choose,
 * rather than being fixed to the opening of segment 0, and it travels in the
 * same `constraints` array as the root2d so there is one shape to reason about.
 *
 * Rotation ONLY: kimodo rejects `positions` on a fullbody target (422; dense
 * position pinning is unstable). It needs no companion root2d coverage, and
 * needs no root seeding anyway — playback is already a delta from the clip's own
 * frame 0.
 *
 * Global-orientation quaternions in xyzw, which is THREE's own component order,
 * read straight off the live rig by FK. World orientation equals the
 * group-local one under the assumption bodyMotionClip already documents (that
 * `__AVATAR_GROUP__` is axis-aligned) — see currentAvatarFacingXZ for what a
 * non-zero group yaw breaks. Same single assumption, not a new one.
 *
 * SOFT, like every kimodo constraint: it is guidance scaled by
 * `constraint_weight`, not an IK solve or a bit-exact imputation, so the opening
 * frames land NEAR this pose rather than on it. Note the weight is SHARED with
 * the root2d in the same request — tightening the pose lock tightens the path
 * with it.
 *
 * @param {number} [frames=DEFAULT_POSE_LOCK_FRAMES]  how many opening frames hold it
 * @returns {object|null} a `fullbody` constraint, or null with no rig to read
 */
function currentPoseConstraint({
  startFrames = DEFAULT_POSE_LOCK_FRAMES,
  endFrames = DEFAULT_POSE_LOCK_FRAMES,
  nFrames = 0,
  yawDelta = 0,
} = {}) {
  const scene = typeof window !== 'undefined' ? window.__AVATAR_SCENE__ : null
  if (!scene || typeof scene.getObjectByName !== 'function') return null
  const q = new THREE.Quaternion()
  const joints = []
  const pose = []
  for (const name of POSE_LOCK_BONES) {
    const bone = scene.getObjectByName(name)
    if (!bone) continue
    bone.getWorldQuaternion(q)
    // A near-zero or non-finite quat is a 422; skip the bone rather than send it.
    if (!Number.isFinite(q.x) || !Number.isFinite(q.w)) continue
    if (Math.hypot(q.x, q.y, q.z, q.w) <= 1e-6) continue
    joints.push(name)
    pose.push([q.x, q.y, q.z, q.w])
  }
  if (joints.length === 0) return null

  const nStart = Math.max(0, Math.round(startFrames))
  const nEnd = Math.max(0, Math.round(endFrames))
  const frameList = []
  for (let f = 0; f < nStart; f++) frameList.push(f)
  // Closing frames, mirroring the opening ones: the clip is pulled back toward a
  // standing pose so it does not end mid-stride. Playback makes this matter more
  // than it looks — a generated walk commits its root travel, and that path sets
  // blendOutMs to 0 (bodyMotionClip), so the end is a HARD CUT back to the base
  // pose with nothing to hide a mid-stride finish.
  if (nEnd > 0 && nFrames > 0) {
    for (let f = Math.max(nStart, nFrames - nEnd); f < nFrames; f++) frameList.push(f)
  }
  if (frameList.length === 0) return null

  // The closing pose is the opening one turned by the route's net change of
  // facing. It has to be: these are GLOBAL orientations, so re-using the opening
  // quats verbatim would ask the character to finish a route that turns a corner
  // still facing the way it set off.
  const endPose = Math.abs(yawDelta) > 1e-6
    ? (() => {
        const yaw = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yawDelta)
        return pose.map(qq => {
          const q2 = yaw.clone().multiply(new THREE.Quaternion(qq[0], qq[1], qq[2], qq[3]))
          return [q2.x, q2.y, q2.z, q2.w]
        })
      })()
    : pose

  return {
    type: 'fullbody',
    joints,
    frames: frameList,
    // One entry per frame, each a per-joint list — the shape the validator wants
    // (`_check_ee_targets`). Fresh arrays per frame; kimodo reads them
    // independently and a shared reference would be a trap for anyone later
    // tapering the pose across the hold.
    rotations: frameList.map(f => (f < nStart ? pose : endPose).map(qq => qq.slice())),
  }
}

/**
 * How far the route turns, in radians about Y, from the character's current
 * facing to the facing its LAST anchor asks for.
 *
 * Read back off the built constraint rather than recomputed from the path, so it
 * cannot disagree with the heading actually sent — including the opening ramp.
 * The stored form is kimodo's hip-line `[fz, fx]` in the CHARACTER's frame, so
 * it needs both the un-swap and the un-rotate.
 */
function headingYawDelta(constraint, startFacing, frameYaw = 0) {
  if (!constraint?.heading?.length || !startFacing) return 0
  const [hz, hx] = constraint.heading[constraint.heading.length - 1]
  // Back to WORLD before comparing: the stored heading is in the character's
  // frame and startFacing is world, and subtracting one from the other would
  // fold the group's yaw into the turn — putting a rotation into the closing
  // pose of a walk that goes straight ahead.
  const [wx, wz] = rotXZ(hx, hz, frameYaw)
  const to = Math.atan2(wx, wz)
  const from = Math.atan2(startFacing[0], startFacing[1])
  let d = (to - from) % (Math.PI * 2)
  if (d > Math.PI) d -= Math.PI * 2
  if (d <= -Math.PI) d += Math.PI * 2
  return d
}

/**
 * The character's CURRENT world facing as an XZ unit vector [fx, fz], or null
 * when there is no avatar group to read.
 *
 * The avatar's yaw lives on the mounted root's `rotation.y` (GLB group or splat root) — that is what
 * movementComponent writes when it turns the character (`targetYaw =
 * atan2(dirX, dirZ)`), so forward for a yaw of theta is (sin theta, cos theta).
 *
 * NOTE, and it is bigger than this function: a non-zero group yaw also means the
 * generated clip is played ROTATED. buildBodyMotionClip commits its root travel
 * in raw world metres on the explicit assumption that the group is "a
 * world-space, axis-aligned wrapper" (bodyMotionClip.js), while the bone tracks
 * driving that same travel sit INSIDE the rotated group. The two only agree at
 * yaw 0. A kimodo walk taken after a movementComponent walk therefore travels
 * off its planned route by exactly the yaw movement left behind. Reading the
 * facing here does not fix that; it is where the problem became visible.
 */
function currentAvatarFacingXZ() {
  const g = resolveAvatarTransformTarget()
  if (!g || !g.rotation) return null
  const y = g.rotation.y
  return [Math.sin(y), Math.cos(y)]
}

/**
 * Generate a prompt-driven body animation that follows a route through an
 * ordered list of world waypoints, then play it.
 *
 * v1 (locked): the whole route is planned into ONE concatenated polyline and
 * sent as ONE kimodo /generate call. Per-segment generation was rejected —
 * kimodo restarts its root from the origin each call, so stitching segments
 * pops the gait at every joint. waypoints[0] is the planning origin (there is
 * no separate `from`).
 *
 *   await __GEN_BODY_MOTION_ON_WAYPOINTS__({ prompt: 'walk', waypoints: [[0,0,0],[2,0,0],[2,0,3]] })
 *
 * @param {object} o
 * @param {string} o.prompt        motion description (should describe locomotion)
 * @param {number[][]} o.waypoints [[x,y,z], …] ordered route (length >= 2);
 *   waypoints[0] is the start.
 * @param {'navmesh'|'straight'} [o.pathMode='navmesh']  'navmesh' plans each
 *   consecutive pair via the environment navmesh; 'straight' is an EXPLICIT
 *   obstacle-ignoring mode (each segment is just [a, b]). navmesh mode throws
 *   loudly when the navmesh is absent — never a silent straight-line fallback.
 * @param {'none'|'curve'} [o.smooth='none']  shape of the planned route before
 *   it becomes the root2d constraint. 'curve' rounds the corners of the whole
 *   route — waypoints included, only its two endpoints held exactly. On a
 *   4-connected grid this is what stops the heading channel alternating 0/90
 *   degrees along a diagonal (see smoothRoutePath). Default 'none' = the raw
 *   polyline, i.e. unchanged behaviour.
 * @param {number} [o.walk_speed]  m/s used to derive duration (default 1.3,
 *   ~natural walk; the model's own cadence). Ignored if duration_sec set.
 * @param {number} [o.duration_sec] clip seconds. Default: route length /
 *   walk_speed so the gait matches the travel (a fixed duration makes short
 *   routes creep and foot-skate). Clamped to [1.5, 15] s.
 * @param {number} [o.constraint_weight] root2d CFG scale (default
 *   DEFAULT_CONSTRAINT_WEIGHT). Kept low deliberately — the constraint carries
 *   BOTH path (position) and facing (heading), and heading is a strong channel;
 *   at weight ≥2 it fights the gait. Authored in the wizard's Waypoints section.
 * @param {number} [o.constraint_stride] send a root2d anchor every N frames
 *   instead of all (default DEFAULT_CONSTRAINT_STRIDE = 3, sparse — ENG-1050
 *   raised it from 1 so the model interpolates its own motion between
 *   anchors; this line still said 1). Endpoints +
 *   every waypoint frame are always included, so a sparse stride still hits
 *   every point the user placed. Larger = smoother gait, looser path tracking.
 *   Rounded and floored at 1; the value actually used comes back as
 *   `constraintStride`.
 * @param {number} [o.seed]        kimodo seed (omitted from the request when unset).
 * @param {number} [o.diffusion_steps]   default 100
 * @param {boolean} [o.continuity]  continue the LIVE avatar (default true): ease
 *   the heading out of its current facing and pin the opening/closing frames to
 *   its current pose. Pass false when authoring a clip that will be saved and
 *   replayed — it then starts directly on the route's own heading.
 * @param {*} [o.play]             playback opts; pass `false` to skip playback
 *   (the wizard's preview-later path) — checked with `=== false`, not falsiness.
 * @param {number} [o.goalTolerance]  per-waypoint reached threshold (default 0.75 m)
 * @returns {Promise<object>} {
 *   tracks, pathMode, path, segments, routeLength, durationSec,
 *   waypointsRequested, waypoints, perWaypoint, goalDist, reachedGoal, clipName,
 *   constraintStride, constraintWeight, anchorFrames, startFacing, frameYaw,
 *   poseLockJoints, smooth, rawRouteLength
 * } — `waypoints` is kimodo's realized root path (per frame, world [x,y,z]),
 * UNsnapped; `path` is the planned route polyline; `perWaypoint` reports how
 * close the realized root landed to each requested waypoint at its pinned frame.
 */
async function genBodyMotionOnWaypoints(o = {}) {
  if (!o.prompt) throw new Error('__GEN_BODY_MOTION_ON_WAYPOINTS__: prompt required')
  if (!Array.isArray(o.waypoints) || o.waypoints.length < 2) {
    throw new Error('__GEN_BODY_MOTION_ON_WAYPOINTS__: waypoints [[x,y,z], …] (>= 2) required')
  }

  const pathMode = o.pathMode ?? 'navmesh'
  // navmesh mode consumes __NAVMESH_INSTANCE__ (getNavmesh throws loudly when
  // it's null — design rule 4: navmesh is never a silent straight-line fallback).
  // Smoothing is a property of the ROUTE, so it is resolved here and reported
  // back: a caller that previewed a route must be able to regenerate the same
  // shape, and walk-here plans its preview and its request separately.
  const smooth = o.smooth ?? DEFAULT_SMOOTH
  const route = pathMode === 'navmesh'
    ? planWaypointRoute(getNavmesh(), o.waypoints, { mode: 'navmesh', smooth })
    : planWaypointRoute(null, o.waypoints, { mode: pathMode, smooth })

  // Duration: route length at a natural walk speed so the gait matches travel.
  const walkSpeed = o.walk_speed ?? 1.3 // m/s, ~natural walk (matches the model)
  const durationSec = o.duration_sec ?? clampRouteDuration(route.length, walkSpeed)
  const constraintStride = Math.max(1, Math.round(o.constraint_stride ?? DEFAULT_CONSTRAINT_STRIDE))
  // Clamped into kimodo's accepted range: 0 is a 422, not an unguided run.
  const constraintWeight = clampConstraintWeight(o.constraint_weight ?? DEFAULT_CONSTRAINT_WEIGHT)
  // Read the facing at generate time, not at plan time: the wizard can relocate
  // the avatar between the two, and this must describe the pose the clip will
  // actually start from.
  // The avatar group's yaw IS the world->kimodo rotation: the generated clip
  // plays inside that group, so everything the request describes has to be
  // expressed in its frame. Zero on a fresh avatar, non-zero the moment a
  // generated walk has turned it — which is why the effect showed up on the
  // second click and not the first.
  // The frame the request is expressed in.
  //
  // Continuing the live avatar: its group's yaw, because the clip is played
  // inside that group and has to line up with the character already standing there.
  //
  // Authoring a standalone clip: the ROUTE's own opening heading instead. In that
  // frame the walk sets off straight ahead, so the clip's first frame faces the
  // rig's forward — the same way an idle does. That matters at PLAYBACK, not in
  // the request: the clip crossfades in from whatever pose is running, and if its
  // opening facing sits at an angle inside the group, the body visibly turns
  // through that angle during the blend. Removing the heading ramp did not stop
  // that, because the turn was never in the constraint. The caller orients the
  // group by the returned `frameYaw` to put the walk back in the world.
  const groupYaw = resolveAvatarTransformTarget()?.rotation?.y || 0
  const routeStartYaw = (() => {
    const a = route.path[0], b = route.path[1]
    if (!a || !b) return 0
    const dx = b[0] - a[0], dz = b[2] - a[2]
    return Math.hypot(dx, dz) > 1e-9 ? Math.atan2(dx, dz) : 0
  })()
  // Left in WORLD. The ramp interpolates it against the path's own tangent,
  // which is world too, and buildRoot2dConstraint rotates the finished heading
  // into the character's frame in one place at the end. Converting here as well
  // mixed the two frames — the ramp then swung from the character's facing to a
  // world angle and back, so a character already pointing along its route was
  // still asked to turn.
  // CONTINUITY — whether this clip continues the live avatar or stands alone.
  // It governs the two things that only make sense for the first:
  //   * the heading ramp, which eases out of the facing the character is in
  //     right now. With none, the clip simply starts on the route's own heading.
  //   * the pose pin on the opening and closing frames.
  // ON for walk-here, which continues whatever the avatar is doing. OFF for the
  // wizard, which authors a clip that is SAVED and replayed from arbitrary
  // states: there is no "current" facing or pose for it to continue from, and
  // pinning one bakes a one-off opening into a reusable animation — which is why
  // an authored walk was starting neutral and turning into its route.
  const continuity = o.continuity !== false
  const startFacing = continuity ? currentAvatarFacingXZ() : null
  const frameYaw = continuity ? groupYaw : routeStartYaw
  // The one builder the path preview also calls, so what is drawn on screen and
  // what is sent cannot drift apart.
  // route.waypoints, not o.waypoints: navmesh planning snaps each point onto
  // the mesh, and pinning a frame for a point the route never passes through
  // picks an arbitrary frame and then reports a huge miss for a correct walk.
  const planPoints = route.waypoints || o.waypoints
  const { constraint, originX, originZ, nFrames, pinFrames } = buildRouteConstraint(route.path, planPoints, {
    durationSec,
    stride: constraintStride,
    startFacing,
    frameYaw,
  })
  // Pose continuity at BOTH ends: hold the opening frames at the pose the avatar
  // is already in, and the closing frames at that pose turned by the route's net
  // change of facing. `pose_lock: null` opts out.
  const netYaw = headingYawDelta(constraint, startFacing, frameYaw)
  const poseLock = continuity ? currentPoseConstraint({ nFrames, yawDelta: netYaw }) : null
  const body = {
    prompt: o.prompt,
    duration_sec: durationSec,
    diffusion_steps: o.diffusion_steps ?? 100,
    constraint_weight: constraintWeight,
    constraints: poseLock ? [constraint, poseLock] : [constraint],
    ...(o.seed != null ? { seed: o.seed } : {}),
  }

  // The clip is BAKED against the rig it plays on, so on a splat character the part
  // rig has to be mounted before playTracks can build anything — asked for here, in
  // parallel with the generation, so the download costs no extra wall clock. A no-op
  // on a GLB avatar and on a rig that is already up.
  const rigReady = ensureLiveBodyRig()

  // Generate; translate the realized root from the generation frame back to
  // world (playback is delta-from-frame0, so the constant offset only matters
  // for the realized/viz world coords).
  const tracks = await postGenerate(body)
  await rigReady
  // Back to world: undo the frame rotation, then the origin re-reference. The
  // tracks themselves are LEFT in the character's frame — they are played inside
  // the group, which supplies that rotation — so this only affects the realized
  // path reported to callers and drawn on screen.
  const realizedWorld = []
  for (let f = 0; f * 3 + 2 < (tracks.root || []).length; f++) {
    const [wx, wz] = rotXZ(tracks.root[f * 3], tracks.root[f * 3 + 2], frameYaw)
    realizedWorld.push([wx + originX, tracks.root[f * 3 + 1], wz + originZ])
  }

  // Realized path = kimodo's root track verbatim (world XZ + pelvis Y), UNsnapped
  // (kimodo is navmesh-agnostic; the soft root2d keeps it within a few cm).
  const realized = realizedWorld
  // A response with no root track can't be measured against the waypoints — fail
  // loud and named rather than letting the perWaypoint map deref undefined.
  if (realized.length === 0) throw new Error('kimodo response has no root track')

  // Per-waypoint reached readout: how close the realized root came to each
  // requested waypoint at that waypoint's pinned frame.
  const tol = o.goalTolerance ?? 0.75
  const perWaypoint = planPoints.map((w, k) => {
    const f = Math.min(pinFrames[k], realized.length - 1)
    const r = realized[f] || realized[realized.length - 1]
    const d = distXZ(r, w)
    return {
      index: k, frame: pinFrames[k], target: [w[0], w[2]], realized: [r[0], r[2]], dist: d, reached: d <= tol,
      // How far this point moved to reach the mesh. Reported so a caller can tell
      // "the walk missed" from "the goal was somewhere the character cannot stand".
      snapDrift: route.snapDrift ? route.snapDrift[k] : 0,
    }
  })
  const goalDist = perWaypoint[perWaypoint.length - 1].dist
  const reachedGoal = goalDist <= tol

  // Play the raw (unsnapped) clip as a first-class mixer action, unless the
  // caller opted out with play:false (the wizard's preview-later path).
  const clipName = o.play === false ? null : playTracks(tracks, o.play)

  const result = {
    tracks, pathMode, path: route.path, segments: route.segments, routeLength: route.length,
    durationSec, waypointsRequested: o.waypoints, waypointsPlanned: planPoints,
    snapDrift: route.snapDrift ?? null, waypoints: realized, perWaypoint,
    goalDist, reachedGoal, clipName, smooth, rawRouteLength: route.rawLength,
    // The tracking settings the request ACTUALLY used (after the stride floor /
    // rounding), so the wizard records what was generated rather than what was
    // typed — a provenance round-trip must reproduce the clip, not the input box.
    constraintStride, constraintWeight, anchorFrames: constraint.frames.length,
    startFacing, frameYaw, continuity, poseLockJoints: poseLock ? poseLock.joints.length : 0,
  }
  console.log(
    `[bodyMotion] waypoint clip: ${o.waypoints.length} waypoints / ${route.segments.length} segments -> ` +
    `${nFrames}f, ${constraint.frames.length} anchors (stride ${constraintStride}, weight ${constraintWeight}` +
    (continuity ? ', continuity' : '') +
    (poseLock ? `, pose lock ${poseLock.joints.length}j x ${poseLock.frames.length}f` : '') + `), ` +
    `smooth=${smooth} ${route.length.toFixed(2)}m` +
    (smooth !== 'none' ? ` (raw ${route.rawLength.toFixed(2)}m)` : '') +
    `, goalDist=${goalDist.toFixed(2)}m`,
  )
  if (typeof window !== 'undefined') window.__LAST_WAYPOINT_BODY_MOTION__ = result
  return result
}

/**
 * Generate a prompt-driven body animation that follows a navmesh path from a
 * start to a goal, then play it. Thin 2-waypoint delegate to
 * genBodyMotionOnWaypoints — kept for its existing hook + return contract.
 *
 *   await __GEN_BODY_MOTION_ON_NAVMESH__({ prompt: 'walk forward', goal: [3, 0, 2] })
 *
 * @param {object} o
 * @param {string} o.prompt        motion description (should describe locomotion)
 * @param {number[]} o.goal        [x,y,z] destination in world meters
 * @param {number[]} [o.from]      [x,y,z] start (default: current avatar position)
 * @param {number} [o.constraint_weight] root2d CFG scale (default DEFAULT_CONSTRAINT_WEIGHT).
 * @param {number} [o.duration_sec] clip seconds. Default: path length / walk_speed.
 * @param {number} [o.walk_speed]  m/s used to derive duration (default 1.3).
 * @param {number} [o.constraint_stride] send a root2d anchor every N frames
 *   (default DEFAULT_CONSTRAINT_STRIDE).
 * @param {number} [o.diffusion_steps]   default 100
 * @returns {Promise<object>} {
 *   tracks, path, waypoints, reachedGoal, goalDist, clipName
 * } — `waypoints` is kimodo's realized root path (per frame, world [x,y,z]),
 * UNsnapped. `path` is the planned navmesh route sent in.
 */
async function genBodyMotionOnNavmesh(o = {}) {
  const { prompt, goal } = o
  if (!prompt) throw new Error('__GEN_BODY_MOTION_ON_NAVMESH__: prompt required')
  if (!Array.isArray(goal) || goal.length < 3) throw new Error('__GEN_BODY_MOTION_ON_NAVMESH__: goal [x,y,z] required')

  // getNavmesh() FIRST so a missing navmesh reports before an unmounted avatar —
  // preserving the legacy error ordering even though the delegate re-checks it.
  getNavmesh()
  const from = o.from || currentAvatarXZ()

  const res = await genBodyMotionOnWaypoints({ ...o, waypoints: [from, goal], pathMode: 'navmesh' })
  const result = {
    tracks: res.tracks, path: res.path, waypoints: res.waypoints,
    goalDist: res.goalDist, clipName: res.clipName, reachedGoal: res.reachedGoal,
  }
  if (typeof window !== 'undefined') window.__LAST_NAV_BODY_MOTION__ = result
  return result
}

if (typeof window !== 'undefined') {
  window.__GEN_BODY_MOTION__ = genBodyMotion
  window.__GEN_BODY_MOTION_ON_NAVMESH__ = genBodyMotionOnNavmesh
  window.__GEN_BODY_MOTION_ON_WAYPOINTS__ = genBodyMotionOnWaypoints
}

export { genBodyMotion, genBodyMotionOnNavmesh, genBodyMotionOnWaypoints, currentAvatarXZ, currentAvatarFacingXZ, currentPoseConstraint, postGenerate, playTracks, stopPreview }
